import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { X_URL, GMGN_URL } from "../config";
import { scrollToId } from "../lib/scroll";

const NAV_LINKS = [
    { label: "HOME", id: "home" },
    { label: "ABOUT", id: "about" },
    { label: "DEV", id: "dev" },
    { label: "ROADMAP", id: "roadmap" },
    { label: "TOKENOMICS", id: "tokenomics" },
    { label: "BUY $BALD", id: "buy" },
    { label: "COMMUNITY", id: "community" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 120 : 0);
    };

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "border-b border-cyan-400/15 bg-[#04060D]/80 py-3 backdrop-blur-xl"
                    : "bg-transparent py-5"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
                <button
                    type="button"
                    data-testid="nav-logo-button"
                    onClick={() => go("home")}
                    className="flex items-center gap-3"
                    aria-label="BALD PIGEON home"
                >
                    <img
                        src="/images/brand/logo-64.png"
                        alt="BALD PIGEON logo"
                        className="h-10 w-10 rounded-full ring-2 ring-cyan-400/40"
                        loading="eager"
                    />
                    <span className="hidden font-display text-lg uppercase tracking-wide text-white sm:block">
                        BALD PIGEON
                    </span>
                    <span className="rounded-full border border-cyan-400/40 px-2 py-0.5 font-mono text-[11px] font-bold text-cyan-300">
                        $BALD
                    </span>
                </button>

                <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            type="button"
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="font-mono text-[13px] font-bold uppercase tracking-widest text-slate-300 transition-colors hover:text-cyan-300"
                        >
                            {l.label}
                        </button>
                    ))}
                    <a
                        href={GMGN_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-buy-bald-button"
                        className="btn-primary !px-5 !py-2.5 text-sm"
                    >
                        BUY $BALD
                    </a>
                    <a
                        href={X_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-join-x-button"
                        className="btn-outline-purple !px-5 !py-2.5 text-sm"
                    >
                        JOIN X
                    </a>
                </nav>

                <button
                    type="button"
                    data-testid="nav-hamburger-button"
                    className="rounded-lg border border-white/15 p-2 text-white lg:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25 }}
                        className="mx-4 mt-2 rounded-2xl border border-cyan-400/15 bg-[#04060D]/95 p-4 backdrop-blur-xl lg:hidden"
                        aria-label="Mobile"
                    >
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.id}
                                type="button"
                                data-testid={`nav-mobile-link-${l.id}`}
                                onClick={() => go(l.id)}
                                className="block w-full rounded-lg px-3 py-3 text-left font-mono text-sm font-bold uppercase tracking-widest text-slate-200 hover:bg-cyan-400/10 hover:text-cyan-300"
                            >
                                {l.label}
                            </button>
                        ))}
                        <div className="mt-3 flex flex-col gap-2">
                            <a
                                href={GMGN_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="nav-mobile-buy-button"
                                className="btn-primary w-full"
                            >
                                BUY $BALD
                            </a>
                            <a
                                href={X_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="nav-mobile-join-x-button"
                                className="btn-outline-purple w-full"
                            >
                                JOIN X
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
